import React from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getWhatIsMyBusiness } from '../../../Redux/Actions/UserAction';
import { useTranslation } from 'react-i18next';

/* [CF 2026-08-09 #15] Subscription card, rebuilt.
   It used to be a grey slab: plan name, a bare progress bar, then three
   equally-weighted lines of text and a small dark button — nothing told you at
   a glance how long you had left. Now the days-remaining number IS the
   headline, the plan is a chip beside it, the two dates sit in a labelled meta
   row, and the state (active / ending soon / ended) drives one accent colour
   across the chip, the bar and the border. */

const ManagePackage = () => {
  const { t, i18n } = useTranslation();
  /* [CF 2026-08-17] The API hands these back as "17 August 2026" - an English
     month name, printed raw. On the Arabic card that left the month in English
     and, because the string is bidirectional, the day and year swapped visually
     ("August 2026 17"). Format for the active locale instead, and pin the
     result to LTR so a date never reorders itself. Anything unparseable falls
     through to the original string rather than disappearing. */
  const fmtDate = (v) => {
    const raw = String(v ?? '').trim();
    if (!raw) return '\u2014';
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    try {
      return d.toLocaleDateString(
        i18n.language && i18n.language.toLowerCase().startsWith('ar') ? 'ar' : 'en-GB',
        { day: 'numeric', month: 'long', year: 'numeric' }
      );
    } catch (e) {
      return raw;
    }
  };

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getWhatIsMyBusiness());
  }, [dispatch]);

  const home = useSelector((state) => state.home);
  const businessDetail = home?.getMybusiness;

  // Percentage of the term already used, clamped — `duration` is 0 on some
  // legacy rows, which used to produce NaN and a bar of indeterminate width.
  const usedPercent = (data) => {
    if (!data || typeof data !== 'object') return 0;
    const { daysLeft, duration } = data;
    if (!duration || !isFinite(duration)) return 0;
    const pct = 100 - (Number(daysLeft) * 100) / Number(duration);
    return Math.max(0, Math.min(100, isFinite(pct) ? pct : 0));
  };

  const daysLeft = Number(businessDetail?.daysLeft);
  const active = daysLeft > 0;
  const endingSoon = active && daysLeft <= 7;
  const state = !active ? 'ended' : endingSoon ? 'soon' : 'active';

  return (
    <div className={`cav-sub-card cav-sub-card--${state}`}>
      {businessDetail?.plan_info && (
        <>
          <div className="cav-sub-head">
            <span className="cav-sub-plan">{businessDetail.plan_info}</span>
            <span className="cav-sub-days">
              {active ? (
                <>
                  <strong>{daysLeft}</strong>
                  <small>{t('days remaining')}</small>
                </>
              ) : (
                <strong className="cav-sub-ended">
                  {t('Subscription Ended')}
                </strong>
              )}
            </span>
          </div>

          <div
            className="cav-sub-bar"
            role="progressbar"
            aria-valuenow={Math.round(usedPercent(businessDetail))}
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <span style={{ width: `${usedPercent(businessDetail)}%` }} />
          </div>

          <dl className="cav-sub-meta">
            <div>
              <dt>{t('Started on')}</dt>
              <dd dir="ltr">{fmtDate(businessDetail?.startDate)}</dd>
            </div>
            <div>
              <dt>{t('Validity till')}</dt>
              <dd dir="ltr">{fmtDate(businessDetail?.expiry)}</dd>
            </div>
          </dl>
        </>
      )}

      <a className="cav-sub-cta" href="#/manage-package" role="button">
        {active ? t('Manage Subscription') : t('Buy Subscription')}
      </a>
    </div>
  );
};

export default ManagePackage;
